import React, { useState, useEffect } from 'react';
import { Card, Form, Space, Radio, Button, message, Tooltip, Checkbox } from 'antd';

import { isEmpty } from 'script/common';
import CommonAppLogo from './CommonAppLogo';
import HideLogo from './HideLogo';

const AppLogoImage = ({ t, settingKey, settingData, onSave }) => {
	const [selectedFiles, setSelectedFiles] = useState({});
	const [selectedOption, setSelectedOption] = useState(1);
	const [checkedDefaults, setCheckedDefaults] = useState({});
	const [form] = Form.useForm();

	useEffect(() => {
		if (!isEmpty(settingData)) {
			const initialFiles = {};
			const initialChecked = {};
			settingKey.keyList.forEach((item) => {
				const dataItem = settingData[item.value];
				if (dataItem && typeof dataItem === 'object' && 'fileName' in dataItem) {
					initialFiles[item.value] = dataItem.fileName || '';
					// 값이 없으면 체크된 상태로 설정
					initialChecked[item.value] = !dataItem.fileName;
				} else {
					initialChecked[item.value] = true; // 값이 없으면 체크
				}
				
			});
			setSelectedFiles(initialFiles);
			setCheckedDefaults(initialChecked); // 체크박스 상태 초기화

			const appLogoImageOption = settingData.appLogoImageOption !== undefined ? settingData.appLogoImageOption : 1;
			setSelectedOption(Number(appLogoImageOption));
			form.setFieldsValue({ option: appLogoImageOption });
		} else {
			setSelectedFiles({});
			setCheckedDefaults({});
			setSelectedOption(1);
		}
	}, [settingData, settingKey.keyList, form]);

	useEffect(() => {
		if (selectedOption === 0) {
			const clearedFiles = {};
			settingKey.keyList.forEach((item) => {
				clearedFiles[item.value] = '';
			});
			setSelectedFiles(clearedFiles);
		} else if (selectedOption === 2) {
			const clearedFiles = {};
			settingKey.keyList.forEach((item) => {
				clearedFiles[item.value] = '';
			});
			setSelectedFiles(clearedFiles);
		} else if (selectedOption === 1 && !isEmpty(settingData)) {
			if (settingData.appLogoImageOption === 2) {
				const clearedFiles = {};
				settingKey.keyList.forEach((item) => {
					clearedFiles[item.value] = '';
				});
				setSelectedFiles(clearedFiles);
			} else {
				const updatedFiles = {};
				settingKey.keyList.forEach((item) => {
					const dataItem = settingData[item.value];
					if (dataItem && typeof dataItem === 'object' && 'fileName' in dataItem) {
						updatedFiles[item.value] = dataItem.fileName || '';
					}
				});
				setSelectedFiles(updatedFiles);
			}
		}
	}, [selectedOption, settingKey.keyList]);

	const onFinish = () => {
		if (selectedOption === 0 && selectedFiles.appLogoImage == null) {
			message.error(t('settings.msg.noFileChosen'));
			return;
		}

		let dataToSave = {
			appLogoImageOption: selectedOption,
		};

		if (selectedOption === 1) {
			settingKey.keyList.forEach(item => {
				if (checkedDefaults[item.value]) {
					dataToSave[item.value] = ''; // 체크박스가 체크되어 있으면 해당 키의 value를 ''로 설정
				} else if (selectedFiles[item.value] instanceof File) {
					dataToSave[item.value] = selectedFiles[item.value];
				}
			});
		} else if (selectedOption === 0 && selectedFiles.appLogoImage === '') {
			dataToSave.appLogoImage = '';
		} else if (selectedOption === 0 && !isEmpty(selectedFiles.appLogoImage)){
			dataToSave.appLogoImage = selectedFiles.appLogoImage
		}
			onSave(dataToSave);
	};

	const handleFileSelect = (file) => {
		setSelectedFiles(file);
		setCheckedDefaults((prev) => ({ ...prev, [settingKey.value]: !file })); // 파일 선택 시 체크박스 해제
	  };

	const handleCheckboxChange = (e, keyValue) => {
		const isChecked = e.target.checked;
		setCheckedDefaults(prev => ({ ...prev, [keyValue]: isChecked }));
		if (isChecked) {
			setSelectedFiles(prev => ({ ...prev, [keyValue]: '' }));
		}
	};

	const handleRadioChange = (e) => {
		const value = e.target.value;
		setSelectedOption(value);
	};

	const onChangeFileInput = (e, keyValue) => {
		const file = e.target.files[0];
		// 파일 입력 초기화
		e.target.value = null;

		if (file) {
			// 파일 크기 제한 체크 (2MB 미만)
			if (file.size > 2 * 1024 * 1024) {
				message.error(t('settings.msg.invalidLogoImage'));
				return;
			}

			// 파일 포맷 체크 (svg, png, jpeg, webp)
			const validFormats = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];
			if (!validFormats.includes(file.type)) {
				message.error(t('settings.msg.invalidLogoImageFormat'));
				return;
			}

			setSelectedFiles(prev => ({ ...prev, [keyValue]: file }));
			setCheckedDefaults((prev) => ({ ...prev, [keyValue]: false })); // 파일 선택 시 체크박스 해제
		}
	};

	return (
		<Form form={form} onFinish={onFinish}>
			<Tooltip title={t('setting.tooltipcontent.appLogoImage.label')} placement="leftTop">
				<Card title={t(settingKey.key)} key={settingKey.key} style={{ margin: 20 }}>
					<Form.Item name='option' style={{ margin: 20 }}>
						<Radio.Group onChange={handleRadioChange} value={selectedOption}>
							<Radio value={0}> {t('settings.radio.label.inherited')} </Radio>
							<Radio value={1}> {t('settings.radio.label.customized')} </Radio>
							<Radio value={2}> {t('settings.radio.label.hideLogo')} </Radio>
						</Radio.Group>
					</Form.Item>

					{selectedOption === 2 ? (
						<HideLogo t={t} />
					) : settingKey.keyList && settingKey.keyList.length > 0 ? (
						selectedOption === 0 ? (
							<CommonAppLogo
                            t={t}
                            onFileSelect={handleFileSelect}
                            fileKey={settingKey.value}
                            settingData={settingData}
                        />
						) : (
						settingKey.keyList.map((item) => (
							<Card type="inner" key={item.key} title={t(item.key)} style={{ margin: 20 }}>
								<Form.Item
									name={['data', item.value, 'value']}
								>
									<div>
										<label id={`label_file_${item.value}`} style={{ margin: 20 }}>
											{selectedFiles[item.value] instanceof File ? selectedFiles[item.value].name : selectedFiles[item.value] || t('settings.msg.noFileChosen')}
										</label>
										<input
											type="file"
											accept=".svg, .png, .jpg, .jpeg, .webp"
											id={`logo_image_${item.value}`}
											onChange={(e) => onChangeFileInput(e, item.value)}
											style={{ display: 'none' }}
										/>
										<Button
											onClick={() => document.getElementById(`logo_image_${item.value}`).click()}
											disabled={selectedOption === 0 || selectedOption === 2}
										>
											{t('settings.button.browseFile')}
										</Button>
										<Checkbox
											checked={checkedDefaults[item.value]}
											onChange={(e) => handleCheckboxChange(e, item.value)}
											style={{ marginLeft: 10 }}
										>
											{t('settings.button.label.useDefaultSettings')}
										</Checkbox>
									</div>
								</Form.Item>
							</Card>
						))
					)
					) : (
						<p>{t('settings.noApps')}</p>
					)}

					<Space style={{ float: 'right', margin: 20 }}>
						<Button type="primary" htmlType="submit" className="btn_text_b">
							{t('user.account.save')}
						</Button>
					</Space>
				</Card>
			</Tooltip>
		</Form>
	);
};

export default AppLogoImage;